import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trFold, KpiCard } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Search, CalendarClock, CheckCircle2, AlertCircle, Factory, Circle, Sun, CalendarDays, FileText, Presentation, Loader2, ListChecks } from "lucide-react";
import { ZoneFilterButtons } from "@/pages/ProfitAnalysisPage";
import { PROGRESS_ITEMS, progressPct } from "@/pages/AssignerPage";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const zoneCls = {
  "Blue Zone": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "Green Zone": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};

const MONTHS_TR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const DAYS_TR = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

const parseDate = (s) => {
  const m = (s || "").trim().match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (!m) return null;
  let [, d, mo, y] = m.map(Number);
  if (y < 100) y += 2000;
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, mo - 1, d);
  return dt.getDate() === d && dt.getMonth() === mo - 1 ? dt : null;
};

const dayDiff = (dt) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((dt - today) / 86400000);
};

export default function KurulumTimelinePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [zoneSel, setZoneSel] = useState("all");
  const [statusSel, setStatusSel] = useState(null);
  const [dlBusy, setDlBusy] = useState(null);
  const [progressFor, setProgressFor] = useState(null);

  const toggleProgress = async (p, item) => {
    const cur = p.kurulum_progress || [];
    const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
    const ordered = PROGRESS_ITEMS.filter((x) => next.includes(x));
    setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, kurulum_progress: ordered } : x)));
    setProgressFor((pf) => (pf && pf.id === p.id ? { ...pf, kurulum_progress: ordered } : pf));
    try {
      await api.patch(`/assigner/projects/${p.id}`, { kurulum_progress: ordered });
    } catch (e) {
      toast.error(apiError(e));
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, kurulum_progress: cur } : x)));
      setProgressFor((pf) => (pf && pf.id === p.id ? { ...pf, kurulum_progress: cur } : pf));
    }
  };

  const downloadReport = async (fmt) => {
    setDlBusy(fmt);
    try {
      const params = {};
      if (zoneSel !== "all") params.zone = zoneSel;
      const res = await api.get(`/assigner/kurulum-report/${fmt}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kurulum-takvimi-ozet-${new Date().toISOString().slice(0, 10)}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(fmt === "pdf" ? "PDF raporu indirildi" : "PowerPoint raporu indirildi");
    } catch {
      toast.error("Rapor oluşturulamadı");
    } finally {
      setDlBusy(null);
    }
  };

  useEffect(() => {
    api.get("/assigner/projects")
      .then((r) => { setRows(r.data.filter((p) => (p.kurulum_tarihi || "").trim())); setLoaded(true); })
      .catch((e) => { toast.error(apiError(e)); setLoaded(true); });
  }, []);

  const baseList = useMemo(() => {
    const f = trFold(q.trim());
    let list = zoneSel === "all" ? rows : rows.filter((p) => p.zone === zoneSel);
    if (f) list = list.filter((p) => trFold(`${p.name} ${p.producer_name || ""} ${p.stand_no || ""} ${p.kurulum_tarihi}`).includes(f));
    return list;
  }, [rows, q, zoneSel]);

  const statusCounts = useMemo(() => {
    const m = new Map();
    baseList.forEach((p) => {
      const s = p.production_status || "Atanmamış";
      m.set(s, (m.get(s) || 0) + 1);
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "tr"));
  }, [baseList]);

  const filtered = useMemo(
    () => (statusSel ? baseList.filter((p) => (p.production_status || "Atanmamış") === statusSel) : baseList),
    [baseList, statusSel]
  );

  const { groups, invalid } = useMemo(() => {
    const m = new Map();
    const bad = [];
    filtered.forEach((p) => {
      const dt = parseDate(p.kurulum_tarihi);
      if (!dt) { bad.push(p); return; }
      const key = dt.getTime();
      if (!m.has(key)) m.set(key, { dt, projects: [] });
      m.get(key).projects.push(p);
    });
    const g = [...m.values()].sort((a, b) => a.dt - b.dt);
    g.forEach((x) => x.projects.sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr")));
    return { groups: g, invalid: bad };
  }, [filtered]);

  const stats = useMemo(() => {
    const diffs = groups.flatMap((g) => g.projects.map(() => dayDiff(g.dt)));
    return {
      total: filtered.length,
      past: diffs.filter((d) => d < 0).length,
      today: diffs.filter((d) => d === 0).length,
      week: diffs.filter((d) => d > 0 && d <= 7).length,
    };
  }, [groups, filtered]);

  const badge = (diff) => {
    if (diff === 0) return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-amber-500 text-white">Bugün</span>;
    if (diff > 0) return <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 bg-blue-500/10 text-blue-600 border border-blue-500/20">{diff} gün kaldı</span>;
    return <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 bg-slate-100 text-slate-500 border border-slate-200">{-diff} gün önce</span>;
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="kurulum-timeline-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><CalendarClock size={10} /> Kurulum Takvimi</div>
          </div>
          <button
            onClick={() => navigate("/atayici")}
            data-testid="kurulum-timeline-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 px-3 py-2 transition-colors duration-150"
          >
            <ArrowLeft size={14} /> Geri Dön
          </button>
        </div>
      </header>
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Kurulum Takvimi</h1>
            <p className="text-sm text-slate-500 mt-1" data-testid="kurulum-timeline-stats">
              {loaded ? <>{stats.total} planlı kurulum{stats.today > 0 && <> · <span className="font-semibold text-amber-600">{stats.today} bugün</span></>}{stats.week > 0 && <> · {stats.week} bu hafta</>}{stats.past > 0 && <> · {stats.past} geçmiş</>}</> : "Yükleniyor..."}
            </p>
            <div className="mt-4">
              <ZoneFilterButtons zones={user?.zones || []} value={zoneSel} onChange={setZoneSel} testidPrefix="kt" />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proje, üretici ara..." data-testid="kurulum-timeline-search" className="rounded-none border-slate-200 h-10 pl-9 w-64 bg-white" />
            </div>
            <button
              type="button"
              onClick={() => downloadReport("pdf")}
              disabled={!!dlBusy}
              data-testid="kurulum-report-pdf"
              className="inline-flex items-center gap-1.5 h-10 px-3 text-sm font-semibold bg-white text-slate-700 border border-slate-300 hover:bg-slate-100 transition-colors duration-150 disabled:opacity-50"
            >
              {dlBusy === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} PDF Rapor
            </button>
            <button
              type="button"
              onClick={() => downloadReport("pptx")}
              disabled={!!dlBusy}
              data-testid="kurulum-report-pptx"
              className="inline-flex items-center gap-1.5 h-10 px-3 text-sm font-semibold bg-slate-900 text-white border border-slate-900 hover:bg-slate-700 transition-colors duration-150 disabled:opacity-50"
            >
              {dlBusy === "pptx" ? <Loader2 size={15} className="animate-spin" /> : <Presentation size={15} />} PPT Sunum
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="kt-kpi-cards">
          <KpiCard icon={CalendarClock} label="Toplam Kurulum" value={baseList.length} accent="bg-slate-900/5 text-slate-700" testid="kt-kpi-total"
            onClick={() => setStatusSel(null)} active={statusSel === null} />
          <KpiCard icon={Sun} label="Bugün" value={stats.today} accent="bg-amber-500/10 text-amber-600" testid="kt-kpi-today" />
          <KpiCard icon={CalendarDays} label="Bu Hafta" value={stats.week} accent="bg-blue-500/10 text-blue-600" testid="kt-kpi-week" />
          {statusCounts.map(([s, c]) => (
            <KpiCard
              key={s}
              icon={s === "Atanmamış" ? Circle : Factory}
              label={s}
              value={c}
              accent={s === "Atanmamış" ? "bg-slate-100 text-slate-400" : "bg-emerald-500/10 text-emerald-600"}
              testid={`kt-kpi-status-${s}`}
              onClick={() => setStatusSel((cur) => (cur === s ? null : s))}
              active={statusSel === s}
            />
          ))}
        </div>

        {loaded && groups.length === 0 && invalid.length === 0 && (
          <div className="bg-white border border-slate-200 px-6 py-12 text-center text-slate-400" data-testid="kurulum-timeline-empty">
            Kurulum tarihi girilmiş proje bulunamadı. Atayıcı tablosundaki "Kurulum Tarihi" sütunundan tarih ekleyebilirsiniz.
          </div>
        )}

        <div className="relative pl-6" data-testid="kurulum-timeline">
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" />
          {groups.map((g) => {
            const diff = dayDiff(g.dt);
            const past = diff < 0;
            return (
              <div key={g.dt.getTime()} className="relative pb-8 last:pb-0" data-testid={`kt-group-${g.dt.getTime()}`}>
                <div className={`absolute -left-6 top-1 w-[15px] h-[15px] border-2 ${
                  diff === 0 ? "bg-amber-500 border-amber-500" : past ? "bg-slate-300 border-slate-300" : "bg-white border-blue-500"
                }`} />
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`font-heading text-lg font-extrabold tracking-tight tabular-nums ${past ? "text-slate-400" : "text-slate-900"}`}>
                    {String(g.dt.getDate()).padStart(2, "0")} {MONTHS_TR[g.dt.getMonth()]} {g.dt.getFullYear()}
                  </span>
                  <span className="text-xs text-slate-400">{DAYS_TR[g.dt.getDay()]}</span>
                  {badge(diff)}
                  <span className="text-xs text-slate-400">{g.projects.length} proje</span>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {g.projects.map((p) => (
                    <div key={p.id} className={`bg-white border border-slate-200 p-4 ${past ? "opacity-60" : ""}`} data-testid={`kt-project-${p.id}`}>
                      <div className="font-semibold text-slate-800 text-sm">{p.name}</div>
                      <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-slate-400 flex-wrap">
                        {p.zone && <span className={`px-1.5 py-0.5 border ${zoneCls[p.zone] || "border-slate-200"}`}>{p.zone}</span>}
                        {p.stand_no && <span>Stand: {p.stand_no}</span>}
                        {p.area_m2 > 0 && <span>{p.area_m2} m²</span>}
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-500">{p.producer_name || "Üretici atanmamış"}</span>
                        {p.production_status && (
                          <span className="inline-flex items-center gap-1 text-slate-500">
                            <CheckCircle2 size={11} className="text-emerald-500" /> {p.production_status}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setProgressFor(p)}
                        data-testid={`kt-progress-btn-${p.id}`}
                        title="Kurulum ilerlemesini işaretle"
                        className="mt-3 w-full flex items-center gap-2 group/ktp"
                      >
                        <div className="flex-1 h-1.5 bg-slate-100 overflow-hidden">
                          <div className={`h-full transition-all duration-300 ${progressPct(p) === 100 ? "bg-emerald-500" : progressPct(p) > 0 ? "bg-blue-500" : ""}`} style={{ width: `${progressPct(p)}%` }} />
                        </div>
                        <span className={`text-xs font-semibold tabular-nums ${progressPct(p) === 100 ? "text-emerald-600" : progressPct(p) > 0 ? "text-slate-700" : "text-slate-300"}`}>%{progressPct(p)}</span>
                        <ListChecks size={13} className="text-slate-300 group-hover/ktp:text-slate-600 transition-colors duration-150" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {invalid.length > 0 && (
          <section className="space-y-3" data-testid="kurulum-timeline-invalid">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5"><AlertCircle size={13} /> Tarih Formatı Okunamayanlar</h2>
            <div className="bg-white border border-slate-200 divide-y divide-slate-100">
              {invalid.map((p) => (
                <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3 text-sm">
                  <span className="font-semibold text-slate-800">{p.name}</span>
                  <span className="text-slate-400 text-xs">"{p.kurulum_tarihi}" — gg.aa.yyyy formatında girin</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <Dialog open={!!progressFor} onOpenChange={(o) => !o && setProgressFor(null)}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="kt-progress-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ListChecks size={18} /> {progressFor?.name}
            </DialogTitle>
          </DialogHeader>
          {progressFor && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-slate-500">Kurulum İlerlemesi</span>
                  <span className={`font-bold tabular-nums ${progressPct(progressFor) === 100 ? "text-emerald-600" : "text-slate-900"}`} data-testid="kt-progress-dialog-pct">
                    %{progressPct(progressFor)}
                  </span>
                </div>
                <div className="h-2 bg-slate-100 overflow-hidden">
                  <div className={`h-full transition-all duration-300 ${progressPct(progressFor) === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${progressPct(progressFor)}%` }} />
                </div>
              </div>
              <div className="space-y-1 max-h-[55vh] overflow-y-auto">
                {PROGRESS_ITEMS.map((item, i) => {
                  const checked = (progressFor.kurulum_progress || []).includes(item);
                  return (
                    <label
                      key={item}
                      className={`flex items-center gap-3 px-3 py-2 border cursor-pointer transition-colors duration-150 ${checked ? "bg-emerald-50/60 border-emerald-200" : "bg-white border-slate-200 hover:border-slate-300"}`}
                      data-testid={`kt-progress-item-${i}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleProgress(progressFor, item)} className="rounded-none" data-testid={`kt-progress-check-${i}`} />
                      <span className={`text-sm ${checked ? "text-slate-800 font-medium" : "text-slate-600"}`}>{item}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
